import { Component, computed, inject, input } from '@angular/core';

import { IconLibrary } from '@finnairoyj/fcom-ui-styles';

import type { DdsLanguage } from '@fcom/core';
import type { CmsArticle, CmsImageData, CmsPicture, CmsVideo } from '@fcom/core-api';
import { CmsContentType } from '@fcom/core-api';
import { symbolToLangPath } from '@fcom/core/utils/cms-path-helpers';
import type { IconName } from '@fcom/ui-components/components/icons/icons.interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { DEFAULT_CMS_HEADING_TAG_LEVEL } from '@fcom/ui-components/components/headers/heading-tag/heading-tag.component';

import { getHeaderLevel } from '../../services/cms-utils/cms-utils';
import { allowedBackgrounds } from '../components/cms-rich-text.component';

@Component({
  selector: 'fin-rich-text',
  styleUrls: ['./rich-text.component.scss'],
  templateUrl: './rich-text.component.html',
  standalone: false,
})
export class RichTextComponent {
  private languageService = inject(LanguageService);

  readonly DEFAULT_HEADER_LEVEL = DEFAULT_CMS_HEADING_TAG_LEVEL;
  readonly IconLibrary = IconLibrary;

  headerLevel = input<number>();
  headerStyles = input<string[]>([]);
  hideMediaContentImage = input(false);
  scaleImage = input(true);

  content = input<CmsArticle>();
  icons = input<Array<{ name: IconName; category?: IconLibrary }>>();
  media = input<CmsPicture[] | CmsVideo[]>();
  styleClasses = input<string[]>();
  title = input<string>();

  resolvedHeaderLevel = computed<number>(() => {
    const headerLevel = this.headerLevel();
    if (headerLevel) {
      return headerLevel;
    }
    const c = this.content();
    return c.headerLevel ?? getHeaderLevel(c?.subjectTaxonomyTags, c?.contentId) ?? 0;
  });

  headerExtraClasses = computed<string>(() => {
    const tags = this.content()?.subjectTaxonomyTags ?? [];
    const headerStyles = this.headerStyles().concat(tags).join(' ');
    const backgroundClass = tags.some((tag) => tag in allowedBackgrounds) ? ' ms-clear-t' : '';

    return headerStyles + backgroundClass;
  });

  mappedContent = computed<string>(() => {
    const ddsLocale: DdsLanguage = this.languageService.ddsLocaleValue as unknown as DdsLanguage;
    const parsedContent: string = this.content()
      .detailText.replace(/<table\b/g, '<div class="rich-text-table-wrapper pr-xsmall-t pr-medium-b"><table')
      .replace(/<\/table>/g, '</table></div>');
    return symbolToLangPath(parsedContent, ddsLocale);
  });

  mappedImage = computed<CmsImageData | undefined>(() => {
    const media = this.media();
    if (media?.length > 0 && media[0].contentType === CmsContentType.CMPicture) {
      return media[0] as CmsPicture;
    }
    return undefined;
  });
}
